import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { VideoUploader } from "@/components/video/VideoUploader";
import { toast } from "sonner";
import { ArrowLeft, Loader2, HelpCircle } from "lucide-react";
import {
  myApplication, myUpdateApplication, myMessages, mySendMessage, SELLER_STAGE, errMsg,
} from "@/lib/applyApi";
import { MessageThread } from "@/components/MessageThread";
import { SaleDateLine } from "@/components/SellerPortal";
import { AgreementSeller } from "@/components/AgreementSeller";
import { StepHorse, validateHorse } from "@/components/apply/Steps";
import { SubmittedPhotos } from "@/components/apply/MediaStep";
import { scrollToFirstError } from "@/lib/scrollToError";
import { genderLabel, breedLabel, disciplineLabels, priceLabel } from "@/lib/horseOptions";
import { locationLabel } from "@/lib/geo";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/brand/AppHeader";
import { StatusLines, AdTemplateButton } from "@/components/AdTemplateStatus";
import { TrainerAccessCard, TrainerContractStatus } from "@/components/seller/TrainerAccess";

import { useAuth } from "@/lib/auth";
import { setPageTitle } from "@/lib/pageTitle";

export default function SellerApplication() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [horse, setHorse] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    myApplication(id).then((d) => { setA(d); setHorse(d.horse); }).catch(() => setNotFound(true));
  }, [id]);
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (a && id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: "start", behavior: "smooth" }), 300);
  }, [a?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (a) setPageTitle(a.status === "approved" ? "Sign Contract" : "My Application");
  }, [a?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback((o) => myMessages(id, o), [id]);
  const send = useCallback((t) => mySendMessage(id, t), [id]);

  const save = async () => {
    const e = validateHorse(horse);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Please fix the highlighted fields before saving.");
      scrollToFirstError(document.querySelector("main"));
      return;
    }
    setBusy(true);
    try {
      const d = await myUpdateApplication(id, { horse });
      setA(d);
      toast.success("Application updated");
    } catch (x) {
      toast.error(errMsg(x, "Could not save"));
    } finally {
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <div data-testid="seller-app-not-found" className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center bg-[#FAF8F5]">
        <h1 className="font-display text-xl font-bold text-stone-900">Application not found</h1>
        <p className="text-sm text-stone-600 max-w-md">This application isn't linked to the account you're signed in with{user?.email ? ` (${user.email})` : ""}. Sign in with the email used on the application to view it.</p>
        <Button data-testid="seller-not-found-back" onClick={() => navigate("/dashboard")} className="rounded-full bg-amber-800 hover:bg-amber-900 text-white">Go to my dashboard</Button>
      </div>
    );
  }
  if (!a) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>;
  const h = a.horse;
  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-10 overflow-x-clip">
      <AppHeader title={h.horse_name || "Your horse"} eyebrow={a.company_name} onBack={() => navigate("/dashboard")} backTestId="seller-back" home="/dashboard" width="max-w-3xl" />
      <main className="max-w-3xl mx-auto px-4 py-5 space-y-5">
        <section className="bg-white border border-stone-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Current stage</div>
          {a.my_role === "trainer" && <p data-testid="seller-role-trainer" className="text-xs font-semibold text-amber-900">You're managing this horse as trainer for {a.contact?.full_name || "the owner"}</p>}
          <div data-testid="seller-stage" className="text-lg font-bold text-amber-900">{SELLER_STAGE[a.status] || a.status}</div>
          {a.status === "approved" && <StatusLines a={a} testId="seller-status" />}
          {a.status === "approved" && <AdTemplateButton a={a} testId="seller-ad-template-btn" />}
          <div className="mt-3 text-xs uppercase tracking-wider text-stone-400 font-semibold">Your horse's sale</div>
          <SaleDateLine s={a.sale_schedule} testId="seller-sale-date" />
          {a.info_request_message && (
            <div data-testid="seller-info-request" className="mt-3 flex gap-2 text-sm text-violet-900 bg-violet-50 border border-violet-200 rounded-lg p-3">
              <HelpCircle className="h-4 w-4 shrink-0 mt-0.5" /><span><strong>We need more information:</strong> {a.info_request_message}</span>
            </div>
          )}
        </section>

        <MessageThread viewer="seller" load={load} send={send} />
        {a.status === "approved" && a.my_role !== "trainer" && <AgreementSeller appId={a.id} />}
        {a.status === "approved" && a.my_role === "trainer" && <TrainerContractStatus a={a} />}
        {a.my_role === "owner" && a.status !== "draft" && <TrainerAccessCard appId={a.id} />}

        {a.status !== "draft" && <SubmittedPhotos app={a} onMediaChange={(fn) => setA((p) => ({ ...p, media: fn(p.media || []) }))} />}

        {a.can_edit ? (
          <section id="seller-edit" data-testid="seller-edit" className="bg-white border border-stone-200 rounded-xl p-4 scroll-mt-20">
            <h2 className="font-semibold text-stone-900 mb-3">Update your horse information</h2>
            <StepHorse horse={horse} set={setHorse} errors={errors} />
            <Button data-testid="seller-save" onClick={save} disabled={busy} className="mt-4 w-full h-12 rounded-full bg-amber-800 hover:bg-amber-900 text-white">Save changes</Button>
            <div className="mt-4"><VideoUploader scope={{ type: "app", id: a.id }} /></div>
          </section>
        ) : (
          <section className="bg-white border border-stone-200 rounded-xl p-4 text-sm">
            <h2 className="font-semibold text-stone-900 mb-2">Your application</h2>
            <p>{[genderLabel(h.gender), breedLabel(h), h.age && `Age ${h.age}`].filter(Boolean).join(" · ")}</p>
            <p className="text-stone-600">{disciplineLabels(h).join(", ")}</p>
            <p className="text-stone-600">{locationLabel(h)}</p>
            {priceLabel(h) && <p data-testid="seller-price" className="text-stone-800 font-semibold">Expected price: {priceLabel(h)}</p>}
            <p className="mt-2 whitespace-pre-wrap text-stone-800">{h.description}</p>
          </section>
        )}
      </main>
    </div>
  );
}
